import React, { useState } from 'react';
import { Modal, Button, Input, message } from 'antd';
import { EditOutlined, CheckCircleOutlined } from '@ant-design/icons';
import drEasyApi from '../../Api/Customapi';

const InvoiceModal = ({ 
  open, 
  onClose, 
  invoiceData, 
  onRefresh 
}) => {
  const [editingInvoice, setEditingInvoice] = useState(false);
  const [editedVisitPrice, setEditedVisitPrice] = useState(0);
  const [editedDiscountAmount, setEditedDiscountAmount] = useState(0);
  const [paymentAmount, setPaymentAmount] = useState(0);
  const [showPaymentInput, setShowPaymentInput] = useState(false);
  const [savingInvoice, setSavingInvoice] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);

  // Initialize edit values when modal opens or invoice data changes
  React.useEffect(() => {
    if (invoiceData) {
      setEditedVisitPrice(invoiceData.visitPrice || 0);
      setEditedDiscountAmount(invoiceData.discountAmount || 0);
    }
  }, [invoiceData]);

  const handleEditInvoice = async () => {
    try {
      setSavingInvoice(true);
      await drEasyApi.put('/api/BookingInvoice/Edit', {
        bookingId: invoiceData.bookingId,
        visitPrice: editedVisitPrice,
        discountAmount: editedDiscountAmount
      });
      
      message.success('Invoice updated successfully');
      setEditingInvoice(false);
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error updating invoice:', error);
      message.error('Failed to update invoice');
    } finally {
      setSavingInvoice(false);
    }
  };

  const handlePayInvoice = async () => {
    try {
      if (paymentAmount <= 0) {
        message.warning('Please enter a valid payment amount');
        return;
      }
      
      const remainingBalance = invoiceData.totalAmount - invoiceData.paidAmount;
      if (paymentAmount > remainingBalance) {
        message.warning('Payment amount cannot exceed remaining balance');
        return;
      }
      
      setProcessingPayment(true);
      await drEasyApi.post('/api/BookingInvoice/Pay', {
        bookingId: invoiceData.bookingId,
        paidAmount: paymentAmount
      });
      
      message.success('Payment recorded successfully');
      setShowPaymentInput(false);
      setPaymentAmount(0);
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error processing payment:', error);
      message.error('Failed to process payment');
    } finally {
      setProcessingPayment(false);
    }
  };

  const handleClose = () => {
    setEditingInvoice(false);
    setShowPaymentInput(false);
    setPaymentAmount(0);
    onClose();
  };

  if (!invoiceData) return null;

  const remainingBalance = invoiceData.totalAmount - invoiceData.paidAmount;
  const isFullyPaid = remainingBalance <= 0;

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      footer={[
        <Button 
          key="edit" 
          icon={<EditOutlined />}
          onClick={() => {
            if (editingInvoice) {
              handleEditInvoice();
            } else {
              setEditingInvoice(true);
            }
          }}
          loading={savingInvoice}
          type={editingInvoice ? "primary" : "default"}
          style={editingInvoice ? { backgroundColor: '#298E9F' } : {}}
        >
          {editingInvoice ? 'Save Changes' : 'Edit Invoice'}
        </Button>,
        editingInvoice && (
          <Button 
            key="cancel-edit"
            onClick={() => {
              setEditingInvoice(false);
              setEditedVisitPrice(invoiceData.visitPrice || 0);
              setEditedDiscountAmount(invoiceData.discountAmount || 0);
            }}
          >
            Cancel Edit
          </Button>
        ),
        <Button 
          key="pay" 
          type="primary"
          style={{ backgroundColor: '#52c41a' }}
          onClick={() => {
            if (showPaymentInput) {
              handlePayInvoice();
            } else {
              setShowPaymentInput(true);
              setPaymentAmount(remainingBalance);
            }
          }}
          loading={processingPayment}
          disabled={editingInvoice || isFullyPaid}
        >
          {showPaymentInput ? 'Confirm Payment' : 'Add Payment'}
        </Button>,
        showPaymentInput && (
          <Button 
            key="cancel-payment"
            onClick={() => {
              setShowPaymentInput(false);
              setPaymentAmount(0);
            }}
          >
            Cancel Payment
          </Button>
        ),
        <Button 
          key="close" 
          onClick={handleClose}
        >
          Close
        </Button>
      ]}
      title={
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-gradient-to-r from-[#298E9F] to-[#1a6b7a] rounded-full flex items-center justify-center">
            <span className="text-white text-lg">💳</span>
          </div>
          <span className="text-lg font-semibold">Booking Invoice</span>
        </div>
      }
      width={700}
      className="invoice-modal"
    >
      <div className="space-y-4">
        {/* Payment Input Section */}
        {showPaymentInput && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <span className="text-green-600">💰</span>
              Record Payment
            </h4>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Payment Amount (LE)
                </label>
                <Input
                  type="number"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(parseFloat(e.target.value) || 0)}
                  prefix={<span className="text-green-600 font-semibold">LE</span>}
                  size="large"
                  min={0}
                  max={remainingBalance}
                  step="0.01"
                  className="rounded-lg"
                />
              </div>
              <div className="flex gap-2">
                <Button 
                  size="small" 
                  onClick={() => setPaymentAmount(remainingBalance / 2)}
                  type="dashed"
                >
                  Half
                </Button>
                <Button 
                  size="small" 
                  onClick={() => setPaymentAmount(remainingBalance)}
                  type="dashed"
                >
                  Full Amount
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Invoice Header - Editable */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600 mb-2 font-medium">Visit Price</p>
              {editingInvoice ? (
                <Input
                  type="number"
                  value={editedVisitPrice}
                  onChange={(e) => setEditedVisitPrice(parseFloat(e.target.value) || 0)}
                  prefix={<span className="text-[#298E9F] font-semibold">LE</span>}
                  size="large"
                  min={0}
                  step="0.01"
                  className="rounded-lg"
                />
              ) : (
                <p className="text-2xl font-bold text-gray-800">
                  {invoiceData.visitPrice?.toFixed(2)} LE
                </p>
              )}
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-2 font-medium">Discount Amount</p>
              {editingInvoice ? (
                <Input
                  type="number"
                  value={editedDiscountAmount}
                  onChange={(e) => setEditedDiscountAmount(parseFloat(e.target.value) || 0)}
                  prefix={<span className="text-orange-600 font-semibold">LE</span>}
                  size="large"
                  min={0}
                  step="0.01"
                  className="rounded-lg"
                />
              ) : (
                <p className="text-2xl font-bold text-orange-600">
                  -{invoiceData.discountAmount?.toFixed(2) || '0.00'} LE
                </p>
              )}
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-blue-300">
            <div className="flex justify-between items-center">
              <p className="text-sm text-gray-600 font-medium">Total Amount</p>
              <p className="text-3xl font-bold text-[#298E9F]">
                {invoiceData.totalAmount?.toFixed(2)} LE
              </p>
            </div>
          </div>
        </div>

        {/* Services List */}
        {invoiceData.services && invoiceData.services.length > 0 && (
          <div>
            <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <span>🏥</span>
              Services Provided
            </h4>
            <div className="space-y-2">
              {invoiceData.services.map((service, index) => (
                <div 
                  key={service.serviceId || index} 
                  className={`p-3 rounded-lg border transition-all ${
                    service.isFromPackage 
                      ? 'bg-green-50 border-green-200 hover:bg-green-100' 
                      : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                  }`}
                >
                  <div className="flex justify-between items-center">
                    <div className="flex-1">
                      <p className="font-medium text-gray-800">{service.title}</p>
                      {service.isFromPackage && (
                        <span className="text-xs text-green-600 font-medium mt-1 inline-block">
                          📦 From Package
                        </span>
                      )}
                    </div>
                    <p className="font-bold text-gray-800 text-lg ml-4">
                      {service.price?.toFixed(2)} LE
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Payment Summary */}
        <div className="border-t-2 border-gray-200 pt-4 space-y-3">
          <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
            <span className="text-gray-700 font-medium">Subtotal:</span>
            <span className="font-semibold text-gray-800 text-lg">
              {(invoiceData.visitPrice + (invoiceData.services?.reduce((sum, s) => sum + s.price, 0) || 0))?.toFixed(2)} LE
            </span>
          </div>

          {invoiceData.discountAmount > 0 && (
            <div className="flex justify-between items-center p-3 bg-orange-50 rounded-lg border border-orange-200">
              <span className="text-orange-700 font-medium">Discount Applied:</span>
              <span className="font-semibold text-orange-600 text-lg">
                -{invoiceData.discountAmount?.toFixed(2)} LE
              </span>
            </div>
          )}

          <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg border border-green-200">
            <span className="text-green-700 font-medium">Amount Paid:</span>
            <span className="font-bold text-green-600 text-xl">
              {invoiceData.paidAmount?.toFixed(2)} LE
            </span>
          </div>
          
          <div className={`flex justify-between items-center p-4 rounded-lg border-2 ${
            isFullyPaid 
              ? 'bg-green-100 border-green-300' 
              : 'bg-orange-100 border-orange-300'
          }`}>
            <span className="font-bold text-gray-800 text-lg">Remaining Balance:</span>
            <span className={`font-bold text-2xl ${
              isFullyPaid ? 'text-green-600' : 'text-orange-600'
            }`}>
              {remainingBalance.toFixed(2)} LE
            </span>
          </div>
          
          {isFullyPaid && (
            <div className="bg-green-100 border-2 border-green-300 rounded-lg p-4 text-center">
              <CheckCircleOutlined className="text-green-600 text-2xl mb-2" />
              <p className="text-green-700 font-bold text-lg">✓ Fully Paid</p>
            </div>
          )}

          {!isFullyPaid && remainingBalance > 0 && (
            <div className="bg-orange-100 border-2 border-orange-300 rounded-lg p-4 text-center">
              <p className="text-orange-800 font-semibold">
                ⚠️ Outstanding Balance: <span className="text-orange-600 text-xl font-bold">{remainingBalance.toFixed(2)} LE</span>
              </p>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default InvoiceModal;